// Vault-Ablage: Dokument reinwerfen, der Server schlaegt Zielordner +
// Dateinamen vor (docs/vault-ablage.md).
//
// Der Server ist authoritative: die Analyse laeuft dort und meldet sich
// ueber den WS-Kanal "vault_inbox". Dieser Store haelt nur den
// gespiegelten Stand -- er rechnet nichts selbst aus und raet nichts
// vorweg.

import { SvelteMap } from "svelte/reactivity";
import { getJSON, postJSON } from "./api";
import { subscribe as wsSubscribe } from "./ws-hub.svelte";

export interface InboxNeighbor {
  path: string;
  similarity: number;
}

export interface InboxCandidate {
  folder: string;
  score: number;
  reasons: string[];
  neighbors?: InboxNeighbor[];
}

export interface InboxSuggestion {
  folder: string;
  filename: string;
  confidence: number;
  reasons: string[];
  candidates: InboxCandidate[];
  sender?: string;
  doc_type?: string;
  doc_date?: string;
  /** Ordner, die schon einmal STATT dieses Vorschlags gewählt wurden. */
  stattdessen?: { folder: string; wann: string }[];
}

export type InboxStatus = "analyzing" | "ready" | "failed" | "committed" | "discarded";

export interface InboxItem {
  id: string;
  filename: string;
  size: number;
  status: InboxStatus;
  error?: string;
  suggestion?: InboxSuggestion;
  preview?: string;
  extractor?: string;
  created: string;
  committed_path?: string;
}

export interface InboxFolder {
  path: string;
  count: number;
}

interface InboxList {
  items: InboxItem[];
  indexed_docs: number;
  index_ready: boolean;
  /** Die Vault-PIN ist eingerichtet und diese Sitzung nicht freigegeben:
   *  die Vorgänge kommen dann OHNE Namen, Vorschlag und Text. */
  locked?: boolean;
}

const _items = new SvelteMap<string, InboxItem>();
let _indexed = $state(0);
let _indexReady = $state(false);
let _folders = $state<InboxFolder[]>([]);
let _recent = $state<string[]>([]);
// Namen nur mit PIN (Tim 09.10.2026): Einwerfen und Scannen gehen ohne
// sie, Vorschlag und Ordnerwahl nicht. Der Server sagt in der Liste, ob
// diese Sitzung gesperrt ist.
let _locked = $state(false);
let _started = false;
let _unsub: (() => void) | undefined;

async function refresh(): Promise<void> {
  try {
    const d = await getJSON<InboxList>("/v2-api/vault/inbox");
    _indexed = d.indexed_docs ?? 0;
    _indexReady = !!d.index_ready;
    _locked = !!d.locked;
    if (_locked) {
      // Was aus einer freigegebenen Zeit noch im Speicher liegt, geht mit
      // der Sperre -- Ordnernamen sind Namen.
      _folders = [];
      _recent = [];
    }
    const seen = new Set<string>();
    for (const it of d.items ?? []) {
      _items.set(it.id, it);
      seen.add(it.id);
    }
    // Serverseitig abgeschlossene Vorgaenge lokal fallen lassen -- der
    // Server ist die Wahrheit darueber, was noch offen ist.
    for (const id of [..._items.keys()]) {
      if (!seen.has(id)) _items.delete(id);
    }
  } catch {
    // Kein Vault/kein Zugriff -- die Karte zeigt dann ihren Leerzustand.
  }
}

export function startVaultInbox(): void {
  if (_started) return;
  _started = true;
  void refresh();
  void loadFolders();
  // Nach einer WS-Luecke (Deploy, Schlafmodus, Netzwechsel) kann der
  // lokale Stand veraltet sein. Beim Zurueckkehren also frisch holen,
  // statt auf einem Geisterstand sitzen zu bleiben (siehe
  // lesson_ws_reconnect_refetch).
  window.addEventListener("v2:ws-open", () => void refresh());
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) void refresh();
  });
  _unsub = wsSubscribe("vault_inbox", (msg: unknown) => {
    // Der Kanal meldet nur, DASS sich etwas geändert hat ({id, status}
    // oder {folders}) -- er geht an jede Sitzung, auch ohne Vault-PIN, und
    // trug bis 09.10.2026 Dateinamen und Vorschläge mit. Den Stand holt
    // die Liste; die prüft die PIN.
    const m = msg as { folders?: boolean; status?: string } | null;
    void refresh();
    // Der Bestand hat sich geändert (ein abgelegtes Dokument ist fertig
    // eingelesen, eines wurde abgelegt oder verworfen): Ordnerliste neu
    // holen. Bis 07.10.2026 geschah das gleich nach dem Ablegen -- bevor
    // der Server so weit war; ein neuer Ordner fehlte dann in der Auswahl.
    if (m?.folders || m?.status === "committed" || m?.status === "discarded") void loadFolders();
  });
}

export function stopVaultInbox(): void {
  _started = false;
  _unsub?.();
  _unsub = undefined;
  _items.clear();
}

/** Ist die Ablage für diese Sitzung gesperrt (Vault-PIN nötig)? */
export function inboxLocked(): boolean {
  return _locked;
}

/** Nach der PIN-Eingabe: Vorgänge mit Namen und die Ordner holen. */
export async function refreshInbox(): Promise<void> {
  await refresh();
  await loadFolders();
}

/** Offene Vorgaenge, aelteste zuerst. */
export function inboxItems(): InboxItem[] {
  return [..._items.values()].sort((a, b) => a.created.localeCompare(b.created));
}

/** Zahl der indizierten Bestandsdokumente (Basis der Vorschlaege). */
export function indexedDocs(): number {
  return _indexed;
}

/** Ist der Dokument-Index befuellt? Ohne ihn gibt es keine Vorschlaege. */
export function indexReady(): boolean {
  return _indexReady;
}

/** ALLE Ordner des Vaults (von der Platte) mit der Zahl der Dokumente
 *  direkt darin -- die Grundlage der Ordner-Auswahl. */
export function inboxFolders(): InboxFolder[] {
  return _folders;
}

/** Die zuletzt über die Ablage benutzten Ordner, jüngster zuerst. */
export function inboxRecent(): string[] {
  return _recent;
}

async function loadFolders(): Promise<void> {
  // Ohne PIN antwortet der Server mit 423 -- gar nicht erst fragen.
  if (_locked) return;
  try {
    const d = await getJSON<{ folders: InboxFolder[]; recent?: string[] }>("/v2-api/vault/inbox/folders");
    _folders = d.folders ?? [];
    _recent = d.recent ?? [];
  } catch {
    // Den alten Stand behalten: eine leere Liste hieße „es gibt keine
    // Ordner", und das stimmt nach einem Aussetzer nicht.
  }
}

/** Ordnerliste frisch holen -- die Auswahl ruft das beim Öffnen. */
export function ladeOrdner(): Promise<void> {
  return loadFolders();
}

/**
 * Laedt ein Dokument hoch und legt es in die Analyse.
 *
 * Kein postJSON: der Upload ist multipart. CSRF laeuft ueber denselben
 * Double-Submit-Header wie sonst.
 */
export async function uploadDocument(file: File): Promise<InboxItem> {
  const form = new FormData();
  form.append("file", file);
  const r = await fetch("/v2-api/vault/inbox", {
    method: "POST",
    credentials: "include",
    headers: csrfHeader(),
    body: form,
  });
  if (!r.ok) {
    let detail = `${r.status}`;
    try {
      const j = await r.json();
      if (typeof j?.error === "string") detail = j.error;
    } catch {
      // keine JSON-Antwort
    }
    throw new Error(detail);
  }
  const item = (await r.json()) as InboxItem;
  _items.set(item.id, item);
  return item;
}

/** Legt das Dokument endgueltig im Vault ab. */
export async function commitDocument(
  id: string,
  folder: string,
  filename: string,
): Promise<{ path: string; new_folder?: boolean; learned?: boolean }> {
  try {
    const res = await postJSON<{ path: string; new_folder?: boolean; learned?: boolean }>(`/v2-api/vault/inbox/${id}/commit`, {
      folder,
      filename,
    });
    _items.delete(id);
    return res;
  } catch (e) {
    // 404 heisst: den Vorgang kennt der Server nicht (mehr). Passiert
    // nach einem Neustart, der die Zwischenablage leert -- der Browser
    // hatte den Eintrag dann noch im Speicher und liess sich nicht
    // davon abbringen (Tim 2026-07-30). Also lokal fallen lassen,
    // statt es endlos anzubieten.
    if (e instanceof Error && /nicht gefunden|404/.test(e.message)) {
      _items.delete(id);
      throw new Error(
        "Dieser Vorgang ist nicht mehr da (z. B. nach einem Neustart) — bitte das Dokument erneut einwerfen.",
      );
    }
    throw e;
  }
}

/** Verwirft einen Vorgang; die Datei in der Zwischenablage wird geloescht. */
export async function discardDocument(id: string): Promise<void> {
  await fetch(`/v2-api/vault/inbox/${id}`, {
    method: "DELETE",
    credentials: "include",
    headers: csrfHeader(),
  });
  _items.delete(id);
}

// CSRF-Header wie in lib/api.ts -- hier nochmal, weil fetch fuer
// multipart/DELETE direkt benutzt wird (postJSON kann kein FormData).
function csrfHeader(): Record<string, string> {
  const all = document.cookie || "";
  for (const part of all.split("; ")) {
    const eq = part.indexOf("=");
    if (eq > 0 && part.slice(0, eq) === "__Host-csrf") {
      return { "X-CSRF-Token": decodeURIComponent(part.slice(eq + 1)) };
    }
  }
  return {};
}
